import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import { getPermissions, getPlatformRoles } from "../repository/data.repository";
import { PermissionActionType } from "../repository/data.default";

export interface PlatformPermissionsForm {
  readonly platformRole: InputObject;
  readonly permission: InputObject;
  readonly allowedAction: InputObject;
  readonly createdByUserUserName: string;
  readonly createdOn: string;
  readonly updatedByUserUserName: string;
  readonly updatedOn: string;
}

export const platformPermissionsViewColumns: DataColumn[] = [
  {
    title: "Role",
    key: "applicationRoleName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Permission",
    key: "permissionName",
    width: 192,
    sort: true,
  },
  {
    title: "Allowed Action",
    key: "allowedAction",
    width: 192,
    sort: true,
  },
  {
    title: "Created By",
    key: "createdByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 192,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
  {
    title: "Updated By",
    key: "updatedByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Updated On",
    key: "updatedOn",
    width: 192,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
];

const platformPermissionsQueryMappings: QueryFieldMapping[] = [
  { formField: "platformRole", queryField: "applicationRoleUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "permission", queryField: "permissionUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "allowedAction", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdByUserUserName" },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "updatedByUserUserName" },
  { formField: "updatedOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapPlatformPermissionsViewQuery = (values: PlatformPermissionsForm): SearchQuery =>
  buildSearchQuery(values, platformPermissionsQueryMappings);

const allowedActionsRequest = (): Promise<InputObject[]> =>
  Promise.resolve(
    Object.entries(PermissionActionType).map(([value, display]) => ({
      display,
      value,
    })),
  );

export const platformPermissionsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "platformRole",
          label: "Role",
          request: createSearchRequest(getPlatformRoles, "name"),
        },
        {
          type: "combobox",
          name: "permission",
          label: "Permission",
          request: createSearchRequest(getPermissions, "name"),
        },
        {
          type: "combobox",
          name: "allowedAction",
          label: "Allowed Action",
          request: allowedActionsRequest,
        },
        { type: "textbox", name: "createdByUserUserName", label: "Created By" },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
        { type: "textbox", name: "updatedByUserUserName", label: "Updated By" },
        { type: "rangetimepicker", name: "updatedOn", label: "Updated On" },
      ],
    },
  ],
};
